import { type PointerEvent as ReactPointerEvent, useEffect, useRef, useState } from "react"
import { drawingToolbar, useDrawingToolbar } from "./drawingToolbarStore.ts"
import { favoritesBarPosition, useToolFavorites } from "./toolFavorites.ts"
import { type BarPosition, clampBarPosition, favoriteTools } from "./toolFavoritesModel.ts"
import { ToolIcon } from "./toolIcons.tsx"

// TradingView's favourite drawing tools bar: shown once a tool is starred in the toolbar's flyouts,
// one button per favourite (click to draw with it, again to put it down), dragged by its grip
// anywhere over the window; this browser remembers where.

const viewport = () => ({ width: window.innerWidth, height: window.innerHeight })

export function FavoriteTools() {
  const tools = favoriteTools(useToolFavorites())
  const toolbar = useDrawingToolbar()
  const [position, setPosition] = useState<BarPosition | null>(() => favoritesBarPosition.load())
  const barRef = useRef<HTMLDivElement>(null)
  const [, resized] = useState(0)

  // A smaller window must not leave the bar outside it.
  useEffect(() => {
    const onResize = () => resized((n) => n + 1)
    window.addEventListener("resize", onResize)
    return () => window.removeEventListener("resize", onResize)
  }, [])

  if (tools.length === 0) return null

  const bar = barRef.current
  const placed = position === null || bar === null
    ? position
    : clampBarPosition(position, { width: bar.offsetWidth, height: bar.offsetHeight }, viewport())

  const startDrag = (event: ReactPointerEvent<HTMLSpanElement>) => {
    const bar = barRef.current
    if (bar === null || event.button !== 0) return
    event.preventDefault()
    const rect = bar.getBoundingClientRect()
    const offset = { x: event.clientX - rect.left, y: event.clientY - rect.top }
    const size = { width: rect.width, height: rect.height }
    let last: BarPosition = { left: rect.left, top: rect.top }
    const onMove = (move: PointerEvent) => {
      last = clampBarPosition({ left: move.clientX - offset.x, top: move.clientY - offset.y }, size, viewport())
      setPosition(last)
    }
    const onUp = () => {
      window.removeEventListener("pointermove", onMove)
      window.removeEventListener("pointerup", onUp)
      window.removeEventListener("pointercancel", onUp)
      favoritesBarPosition.save(last)
    }
    window.addEventListener("pointermove", onMove)
    window.addEventListener("pointerup", onUp)
    window.addEventListener("pointercancel", onUp)
  }

  return (
    <div
      ref={barRef}
      className={`favorite-tools${placed === null ? " favorite-tools-default" : ""}`}
      role="toolbar"
      aria-label="Favorite drawing tools"
      style={placed === null ? undefined : { left: placed.left, top: placed.top }}
    >
      <span className="favorite-tools-grip" title="Drag to move" aria-hidden onPointerDown={startDrag}>
        <svg width="8" height="16" viewBox="0 0 8 16" fill="currentColor">
          {[2, 6, 10, 14].flatMap((y) => [<circle key={`a${y}`} cx="2" cy={y} r="1.1" />, <circle key={`b${y}`} cx="6" cy={y} r="1.1" />])}
        </svg>
      </span>
      {tools.map((tool) => (
        <button
          key={tool.kind}
          type="button"
          aria-label={tool.name}
          title={tool.hotkey ? `${tool.name} (${tool.hotkey})` : tool.name}
          aria-pressed={toolbar.tool === tool.kind}
          onClick={() => drawingToolbar.setTool(toolbar.tool === tool.kind ? null : tool.kind)}
        >
          <ToolIcon kind={tool.kind} />
        </button>
      ))}
    </div>
  )
}
